import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Separator } from '../ui/separator';
import { Loader2, Plus, Trash2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['draft', 'sent', 'paid', 'overdue', 'cancelled', 'void'];

/**
 * Full-featured Super Admin invoice editor. Every editable field on
 * `admin_invoices`, saved via PATCH /api/admin-invoices/:id. Recomputes
 * totals live and lets master admin change status directly.
 */
const InvoiceEditDialog = ({ open, onOpenChange, invoice, headers, onSaved }) => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!invoice || !open) return;
    setForm({
      client_name: invoice.client_name || '',
      client_email: invoice.client_email || '',
      client_phone: invoice.client_phone || '',
      invoice_number: invoice.invoice_number || '',
      service_type: invoice.service_type || '',
      service_description: invoice.service_description || '',
      property_address: invoice.property_address || '',
      billing_address: invoice.billing_address || '',
      bedrooms: invoice.bedrooms || '',
      bathrooms: invoice.bathrooms || '',
      square_footage: invoice.square_footage || '',
      appointment_id: invoice.appointment_id || '',
      status: invoice.status || 'draft',
      payment_status: invoice.payment_status || '',
      payment_terms: invoice.payment_terms || '',
      date_of_service: invoice.date_of_service || '',
      issue_date: invoice.issue_date || (invoice.created_at ? invoice.created_at.slice(0, 10) : ''),
      due_date: invoice.due_date || '',
      notes: invoice.notes || '',
      internal_notes: invoice.internal_notes || '',
      line_items: (invoice.line_items || []).map((li, i) => ({ ...li, _key: `${i}-${Date.now()}` })),
      tax_amount: invoice.tax_amount || 0,
      discount_amount: invoice.discount_amount || 0,
      fees_amount: invoice.fees_amount || 0,
    });
  }, [invoice, open]);

  if (!invoice || !form) return null;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const updateLine = (i, k, v) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [k]: v };
    set('line_items', items);
  };

  const addLine = () => set('line_items', [
    ...form.line_items,
    { _key: `n-${Date.now()}`, description: '', quantity: 1, unit_price: 0 },
  ]);

  const removeLine = (i) => set('line_items', form.line_items.filter((_, idx) => idx !== i));

  const subtotal = form.line_items.reduce(
    (s, li) => s + (parseFloat(li.unit_price) || 0) * (parseInt(li.quantity, 10) || 1), 0,
  );
  const tax = parseFloat(form.tax_amount) || 0;
  const discount = parseFloat(form.discount_amount) || 0;
  const fees = parseFloat(form.fees_amount) || 0;
  const total = Math.max(0, subtotal + tax + fees - discount);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items
          .filter(li => (li.description || '').trim())
          .map(li => ({
            description: li.description,
            quantity: parseInt(li.quantity, 10) || 1,
            unit_price: parseFloat(li.unit_price) || 0,
          })),
        tax_amount: tax,
        discount_amount: discount,
        fees_amount: fees,
      };
      await axios.patch(`${API_URL}/api/admin-invoices/${invoice.id}`, payload, { headers });
      toast.success('Invoice updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {invoice.invoice_number}</DialogTitle>
          <DialogDescription>
            Master admin edit — changes take effect immediately across Customers, Appointments, and Invoices views.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="basics" className="mt-2">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basics" data-testid="tab-basics">Basics</TabsTrigger>
            <TabsTrigger value="items" data-testid="tab-items">Line Items</TabsTrigger>
            <TabsTrigger value="amounts" data-testid="tab-amounts">Amounts</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
          </TabsList>

          {/* BASICS */}
          <TabsContent value="basics" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Invoice #"><Input value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} data-testid="edit-invoice-number" /></Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={v => set('status', v)}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Client Name"><Input value={form.client_name} onChange={e => set('client_name', e.target.value)} data-testid="edit-client-name" /></Field>
              <Field label="Client Email"><Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} data-testid="edit-client-email" /></Field>
              <Field label="Client Phone"><Input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} /></Field>
              <Field label="Payment Terms"><Input value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} placeholder="e.g. Net 30, Due on receipt" /></Field>
              <Field label="Service Type"><Input value={form.service_type} onChange={e => set('service_type', e.target.value)} /></Field>
              <Field label="Appointment ID">
                <Input value={form.appointment_id} onChange={e => set('appointment_id', e.target.value)} placeholder="Link to appointment (optional)" />
              </Field>
              <Field label="Issue Date"><Input type="date" value={form.issue_date?.slice(0, 10) || ''} onChange={e => set('issue_date', e.target.value)} /></Field>
              <Field label="Due Date"><Input type="date" value={form.due_date?.slice(0, 10) || ''} onChange={e => set('due_date', e.target.value)} /></Field>
              <Field label="Date of Service"><Input value={form.date_of_service} onChange={e => set('date_of_service', e.target.value)} placeholder="MM/DD/YYYY" /></Field>
              <Field label="Payment Status Note"><Input value={form.payment_status} onChange={e => set('payment_status', e.target.value)} placeholder="Optional free-text" /></Field>
            </div>
            <Separator />
            <Field label="Service Description">
              <Textarea rows={2} value={form.service_description} onChange={e => set('service_description', e.target.value)} data-testid="edit-service-description" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Service Address (Property)"><Textarea rows={2} value={form.property_address} onChange={e => set('property_address', e.target.value)} data-testid="edit-property-address" /></Field>
              <Field label="Billing Address"><Textarea rows={2} value={form.billing_address} onChange={e => set('billing_address', e.target.value)} data-testid="edit-billing-address" /></Field>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Bedrooms"><Input value={form.bedrooms} onChange={e => set('bedrooms', e.target.value)} /></Field>
              <Field label="Bathrooms"><Input value={form.bathrooms} onChange={e => set('bathrooms', e.target.value)} /></Field>
              <Field label="Sq ft"><Input value={form.square_footage} onChange={e => set('square_footage', e.target.value)} /></Field>
            </div>
          </TabsContent>

          {/* LINE ITEMS */}
          <TabsContent value="items" className="mt-4 space-y-2">
            {form.line_items.map((li, i) => (
              <div key={li._key} className="grid grid-cols-12 gap-2 items-start p-2 rounded-lg bg-zinc-50" data-testid={`line-item-row-${i}`}>
                <div className="col-span-6"><Input placeholder="Description" value={li.description} onChange={e => updateLine(i, 'description', e.target.value)} data-testid={`line-desc-${i}`} /></div>
                <div className="col-span-2"><Input type="number" min="1" placeholder="Qty" value={li.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} data-testid={`line-qty-${i}`} /></div>
                <div className="col-span-3"><Input type="number" step="0.01" placeholder="Unit $" value={li.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} data-testid={`line-unit-${i}`} /></div>
                <Button size="icon" variant="ghost" onClick={() => removeLine(i)} className="col-span-1 text-red-500 h-9 w-9" data-testid={`line-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addLine} className="mt-2" data-testid="add-line-item"><Plus className="w-4 h-4 mr-1" /> Add line item</Button>
          </TabsContent>

          {/* AMOUNTS */}
          <TabsContent value="amounts" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Tax Amount ($)"><Input type="number" step="0.01" value={form.tax_amount} onChange={e => set('tax_amount', e.target.value)} data-testid="edit-tax" /></Field>
              <Field label="Discount ($)"><Input type="number" step="0.01" value={form.discount_amount} onChange={e => set('discount_amount', e.target.value)} data-testid="edit-discount" /></Field>
              <Field label="Fees ($)"><Input type="number" step="0.01" value={form.fees_amount} onChange={e => set('fees_amount', e.target.value)} data-testid="edit-fees" /></Field>
            </div>
            <Separator />
            <div className="bg-zinc-50 rounded-xl p-4 space-y-1 text-sm">
              <Row label="Subtotal" value={subtotal} />
              <Row label="Tax" value={tax} />
              <Row label="Discount" value={-discount} />
              <Row label="Fees" value={fees} />
              <Separator className="my-2" />
              <div className="flex justify-between font-bold text-base">
                <span>New Total</span>
                <span className="text-[#F56F05]" data-testid="edit-new-total">${total.toFixed(2)}</span>
              </div>
            </div>
          </TabsContent>

          {/* NOTES */}
          <TabsContent value="notes" className="mt-4 space-y-3">
            <Field label="Client-visible Notes">
              <Textarea rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="These appear on the invoice sent to the client" data-testid="edit-notes" />
            </Field>
            <Field label="Internal Notes (admin only)">
              <Textarea rows={4} value={form.internal_notes} onChange={e => set('internal_notes', e.target.value)} placeholder="Team-only notes — never sent to client" data-testid="edit-internal-notes" />
            </Field>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t border-zinc-100 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#F56F05] hover:bg-[#e06505] text-white" data-testid="save-invoice-edits">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-zinc-600">{label}</Label>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-zinc-500">{label}</span>
    <span className={value < 0 ? 'text-red-600' : ''}>{value < 0 ? '-' : ''}${Math.abs(value).toFixed(2)}</span>
  </div>
);

export default InvoiceEditDialog;
